import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Sparkles, Check, Phone, Mail, User } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const questions = [
  {
    id: 1,
    question: "What's your main goal with NAD+?",
    options: [
      { value: "lose_weight", label: "Lose weight" },
      { value: "more_energy", label: "More energy" },
      { value: "slow_aging", label: "Slow aging" },
      { value: "all_above", label: "All of the above" }
    ]
  },
  {
    id: 2,
    question: "Have you tried NAD+ before?",
    options: [
      { value: "no_never", label: "No, never" },
      { value: "yes_iv", label: "Yes, IV therapy" },
      { value: "yes_supplements", label: "Yes, supplements" },
      { value: "not_sure", label: "Not sure" }
    ]
  },
  {
    id: 3,
    question: "How would you describe your energy levels?",
    options: [
      { value: "exhausted", label: "Exhausted most days" },
      { value: "up_and_down", label: "Up and down" },
      { value: "pretty_good", label: "Pretty good" },
      { value: "great", label: "Great" }
    ]
  },
  {
    id: 4,
    question: "Are you currently on a GLP-1 (Semaglutide/Tirzepatide)?",
    options: [
      { value: "yes", label: "Yes" },
      { value: "no", label: "No" },
      { value: "interested", label: "Not yet but interested" },
      { value: "whats_that", label: "What's that?" }
    ]
  }
];

const NadQuizPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [contactInfo, setContactInfo] = useState({ name: '', phone: '', email: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState('');

  const totalSteps = questions.length + 1; // questions + contact form
  const progress = ((currentStep + 1) / totalSteps) * 100;

  const handleOptionSelect = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
    // Auto-advance after selection
    setTimeout(() => {
      if (currentStep < questions.length - 1) {
        setCurrentStep(prev => prev + 1);
      } else {
        setCurrentStep(questions.length); // Go to contact form
      }
    }, 300);
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!contactInfo.name.trim()) {
      setError('Please enter your name');
      return;
    }
    if (!contactInfo.phone.trim()) {
      setError('Please enter your phone number');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/nad-leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: contactInfo.name,
          phone: contactInfo.phone,
          email: contactInfo.email || null,
          answers: answers
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit');
      }

      setIsComplete(true);
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Completion screen
  if (isComplete) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #020617 0%, #0c1929 50%, #0f172a 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif'
      }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          style={{
            maxWidth: '500px',
            width: '100%',
            textAlign: 'center',
            padding: '48px 32px',
            background: 'rgba(15, 23, 42, 0.8)',
            borderRadius: '24px',
            border: '1px solid rgba(139, 92, 246, 0.2)',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)'
          }}
        >
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #8b5cf6 0%, #06b6d4 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px'
          }}>
            <Check style={{ width: '40px', height: '40px', color: 'white' }} />
          </div>
          
          <h1 style={{
            fontSize: '28px',
            fontWeight: 800,
            color: '#f8fafc',
            marginBottom: '16px'
          }}>
            Thanks {contactInfo.name}!
          </h1>
          
          <p style={{
            fontSize: '18px',
            color: '#94a3b8',
            lineHeight: 1.6,
            marginBottom: '32px'
          }}>
            Bryan will personally reach out within 24 hours to discuss your NAD+ options.
          </p>
          
          <p style={{
            fontSize: '13px',
            color: '#64748b',
            marginBottom: '32px'
          }}>
            Results vary. Not medical advice.
          </p>
          
          <button
            onClick={() => navigate('/')}
            style={{
              background: 'transparent',
              border: '1px solid rgba(148, 163, 184, 0.3)',
              color: '#94a3b8',
              padding: '12px 24px',
              borderRadius: '999px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.target.style.borderColor = 'rgba(139, 92, 246, 0.5)';
              e.target.style.color = '#c4b5fd';
            }}
            onMouseLeave={(e) => {
              e.target.style.borderColor = 'rgba(148, 163, 184, 0.3)';
              e.target.style.color = '#94a3b8';
            }}
          >
            Return to Home
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #020617 0%, #0c1929 50%, #0f172a 100%)',
      fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif'
    }}>
      {/* Header */}
      <header style={{
        padding: '20px',
        borderBottom: '1px solid rgba(148, 163, 184, 0.1)'
      }}>
        <div style={{
          maxWidth: '600px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <button
            onClick={() => navigate('/')}
            style={{
              background: 'none',
              border: 'none',
              color: '#94a3b8',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px'
            }}
          >
            <ChevronLeft style={{ width: '20px', height: '20px' }} />
            Back
          </button>
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: '#c4b5fd'
          }}>
            <Sparkles style={{ width: '20px', height: '20px' }} />
            <span style={{ fontWeight: 700, fontSize: '14px' }}>NAD+ Assessment</span>
          </div>
          
          <div style={{ width: '60px' }} />
        </div>
      </header>

      {/* Progress Bar */}
      <div style={{
        maxWidth: '600px',
        margin: '0 auto',
        padding: '20px 20px 0'
      }}>
        <div style={{
          height: '4px',
          background: 'rgba(148, 163, 184, 0.2)',
          borderRadius: '2px',
          overflow: 'hidden'
        }}>
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            style={{
              height: '100%',
              background: 'linear-gradient(90deg, #8b5cf6 0%, #06b6d4 100%)',
              borderRadius: '2px'
            }}
          />
        </div>
        <p style={{
          textAlign: 'center',
          color: '#64748b',
          fontSize: '12px',
          marginTop: '8px'
        }}>
          Step {currentStep + 1} of {totalSteps}
        </p>
      </div>

      {/* Content */}
      <div style={{
        maxWidth: '600px',
        margin: '0 auto',
        padding: '40px 20px'
      }}>
        <AnimatePresence mode="wait">
          {currentStep < questions.length ? (
            // Question Step
            <motion.div
              key={`question-${currentStep}`}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <h2 style={{
                fontSize: 'clamp(24px, 5vw, 32px)',
                fontWeight: 800,
                color: '#f8fafc',
                textAlign: 'center',
                marginBottom: '40px',
                lineHeight: 1.3
              }}>
                {questions[currentStep].question}
              </h2>

              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                {questions[currentStep].options.map((option) => {
                  const isSelected = answers[questions[currentStep].id] === option.value;
                  return (
                    <button
                      key={option.value}
                      onClick={() => handleOptionSelect(questions[currentStep].id, option.value)}
                      style={{
                        padding: '20px 24px',
                        background: isSelected 
                          ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(6, 182, 212, 0.2) 100%)'
                          : 'rgba(15, 23, 42, 0.6)',
                        border: isSelected
                          ? '2px solid rgba(139, 92, 246, 0.5)'
                          : '1px solid rgba(148, 163, 184, 0.2)',
                        borderRadius: '16px',
                        color: isSelected ? '#c4b5fd' : '#e2e8f0',
                        fontSize: '16px',
                        fontWeight: 600,
                        textAlign: 'left',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.target.style.borderColor = 'rgba(139, 92, 246, 0.3)';
                          e.target.style.background = 'rgba(15, 23, 42, 0.8)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.target.style.borderColor = 'rgba(148, 163, 184, 0.2)';
                          e.target.style.background = 'rgba(15, 23, 42, 0.6)';
                        }
                      }}
                    >
                      {option.label}
                      {isSelected && <Check style={{ width: '20px', height: '20px' }} />}
                    </button>
                  );
                })}
              </div>

              {currentStep > 0 && (
                <button
                  onClick={handleBack}
                  style={{
                    marginTop: '32px',
                    background: 'none',
                    border: 'none',
                    color: '#64748b',
                    fontSize: '14px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    margin: '32px auto 0'
                  }}
                >
                  <ChevronLeft style={{ width: '16px', height: '16px' }} />
                  Back
                </button>
              )}
            </motion.div>
          ) : (
            // Contact Form Step
            <motion.div
              key="contact-form"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <h2 style={{
                fontSize: 'clamp(24px, 5vw, 32px)',
                fontWeight: 800,
                color: '#f8fafc',
                textAlign: 'center',
                marginBottom: '16px',
                lineHeight: 1.3
              }}>
                Almost done! Where should Bryan reach you?
              </h2>
              
              <p style={{
                color: '#94a3b8',
                textAlign: 'center',
                marginBottom: '32px',
                fontSize: '16px'
              }}>
                Bryan will personally review your answers and reach out within 24 hours.
              </p>

              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: '#94a3b8',
                    fontSize: '14px',
                    marginBottom: '8px'
                  }}>
                    <User style={{ width: '16px', height: '16px' }} />
                    First Name *
                  </label>
                  <input
                    type="text"
                    value={contactInfo.name}
                    onChange={(e) => setContactInfo(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Your first name"
                    style={{
                      width: '100%',
                      padding: '16px 20px',
                      background: 'rgba(15, 23, 42, 0.6)',
                      border: '1px solid rgba(148, 163, 184, 0.2)',
                      borderRadius: '12px',
                      color: '#f8fafc',
                      fontSize: '16px',
                      outline: 'none',
                      transition: 'border-color 0.2s',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = 'rgba(139, 92, 246, 0.5)'}
                    onBlur={(e) => e.target.style.borderColor = 'rgba(148, 163, 184, 0.2)'}
                  />
                </div>

                <div>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: '#94a3b8',
                    fontSize: '14px',
                    marginBottom: '8px'
                  }}>
                    <Phone style={{ width: '16px', height: '16px' }} />
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    value={contactInfo.phone}
                    onChange={(e) => setContactInfo(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="(555) 123-4567"
                    style={{
                      width: '100%',
                      padding: '16px 20px',
                      background: 'rgba(15, 23, 42, 0.6)',
                      border: '1px solid rgba(148, 163, 184, 0.2)',
                      borderRadius: '12px',
                      color: '#f8fafc',
                      fontSize: '16px',
                      outline: 'none',
                      transition: 'border-color 0.2s',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = 'rgba(139, 92, 246, 0.5)'}
                    onBlur={(e) => e.target.style.borderColor = 'rgba(148, 163, 184, 0.2)'}
                  />
                </div>

                <div>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: '#94a3b8',
                    fontSize: '14px',
                    marginBottom: '8px'
                  }}>
                    <Mail style={{ width: '16px', height: '16px' }} />
                    Email (optional)
                  </label>
                  <input
                    type="email"
                    value={contactInfo.email}
                    onChange={(e) => setContactInfo(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="you@example.com"
                    style={{
                      width: '100%',
                      padding: '16px 20px',
                      background: 'rgba(15, 23, 42, 0.6)',
                      border: '1px solid rgba(148, 163, 184, 0.2)',
                      borderRadius: '12px',
                      color: '#f8fafc',
                      fontSize: '16px',
                      outline: 'none',
                      transition: 'border-color 0.2s',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = 'rgba(139, 92, 246, 0.5)'}
                    onBlur={(e) => e.target.style.borderColor = 'rgba(148, 163, 184, 0.2)'}
                  />
                </div>

                {error && (
                  <p style={{
                    color: '#f87171',
                    fontSize: '14px',
                    textAlign: 'center'
                  }}>
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    marginTop: '16px',
                    padding: '18px 32px',
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #06b6d4 100%)',
                    border: 'none',
                    borderRadius: '999px',
                    color: 'white',
                    fontSize: '18px',
                    fontWeight: 700,
                    cursor: isSubmitting ? 'wait' : 'pointer',
                    opacity: isSubmitting ? 0.7 : 1,
                    transition: 'all 0.2s',
                    boxShadow: '0 10px 40px rgba(139, 92, 246, 0.3)'
                  }}
                >
                  {isSubmitting ? 'Submitting...' : 'Get My NAD+ Assessment →'}
                </button>

                <button
                  type="button"
                  onClick={handleBack}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#64748b',
                    fontSize: '14px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    margin: '16px auto 0'
                  }}
                >
                  <ChevronLeft style={{ width: '16px', height: '16px' }} />
                  Back
                </button>
              </form>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Footer */}
      <footer style={{
        padding: '20px',
        textAlign: 'center',
        color: '#475569',
        fontSize: '12px'
      }}>
        Results vary. Not medical advice.
      </footer>
    </div>
  );
};

export default NadQuizPage;
